import { ApiClient } from 'yc-api-client';
import axios from 'axios';
import { get, each } from 'lodash';
import Blokkendoos from './blokkendoos';
import AppError from '../models/AppError';
import { Sentry, getAxiosErrorData } from './sentry';
import { retryFetch } from '../helpers/util';
import { ENDPOINTS, GTM_CONFIGS } from './constants';
import isIsolatedMode from '../helpers/isIsolatedMode';
import IsolatedClient from './isolatedClient';

const RESUME_FILE_TYPE = 'RESUME_FILE';

export default class YCApi {
  constructor({ authToken } = {}) {
    this.client = isIsolatedMode()
      ? new IsolatedClient()
      : new ApiClient(authToken);
    this.blokkendoos = new Blokkendoos();
  }

  submitPartialCandidate(attributes) {
    const signupEventLabel = this.blokkendoos.getSignupEventLabel();
    const apiData = { attributes };
    if (signupEventLabel) {
      apiData.meta = { signup_event_label: signupEventLabel };
    }

    return this.client
      .type(ENDPOINTS.partial_candidates)
      .post(ENDPOINTS.resource, apiData)
      .catch(
        YCApi.captureError({
          extra: { attributes },
          title: ({ message }) => `Signup failed: ${message}`
        })
      )
      .then(resp => {
        YCApi.addGoogleTagManagementData(signupEventLabel);
        return resp;
      })
      .catch(
        YCApi.captureError({
          title: ({ message }) =>
            `Adding Google Tag Management data to dataLayer failed: ${message}`
        })
      );
  }

  updatePartialCandidate(id, attributes) {
    if (!id) throw TypeError('YCApi: id is required');
    if (attributes.password) {
      // Resubmitting the password of a user will cause the user to be logged out.
      throw TypeError(
        'Resubmitting password with candidate update request is not allowed. Remove property before submitting.'
      );
    }
    return this.client
      .type(ENDPOINTS.partial_candidates)
      .patch(ENDPOINTS.resource, { id, attributes })
      .catch(
        YCApi.captureError({
          extra: { id, attributes },
          title: ({ message }) => `Candidate update failed: ${message}`
        })
      );
  }

  submitFile(file) {
    const attributes = {
      type: RESUME_FILE_TYPE,
      file
    };
    return this.client
      .type(ENDPOINTS.file_uploads)
      .put(ENDPOINTS.resource, {
        file_upload: { attributes }
      })
      .catch(
        YCApi.captureError({
          extra: { attributes },
          title: ({ message }) => `File upload failed: ${message}`
        })
      )
      .catch(err => {
        const statusCode = get(err, 'response.status');
        /* this is the path to get error message provided by backend API */
        const fileErrorMessage = get(err, 'response.data[0].original[0]') || '';
        /* files accepted for now */
        const FILE_REGEX = /word|pdf|rtf/i;

        if (statusCode === 422 && fileErrorMessage.match(FILE_REGEX)) {
          throw new AppError(fileErrorMessage);
        }

        throw err;
      });
  }

  checkEmailUniqueness(email) {
    return this.client
      .type(ENDPOINTS.candidates)
      .get(ENDPOINTS.unique_email_check, { params: { email } });
  }

  fetchOptionsList() {
    return retryFetch(5, 3000, () =>
      this.client
        .type(ENDPOINTS.settings_data)
        .get(ENDPOINTS.resource)
        .catch(
          YCApi.captureError({
            title: ({ message }) => `Fetch options failed: ${message}`
          })
        )
    );
  }

  fetchEducationsList(eduName, eduTypeId) {
    const searchParams =
      eduName && eduTypeId
        ? { search: { name: eduName, type_id: eduTypeId } }
        : {};
    return retryFetch(5, 3000, () =>
      this.client
        .type(ENDPOINTS.educations)
        .get(ENDPOINTS.resource, searchParams)
        .catch(
          YCApi.captureError({
            extra: { eduName, eduTypeId },
            title: ({ message }) => `Fetch education options failed: ${message}`
          })
        )
    );
  }

  fetchNetherlandsAddress(address = {}) {
    const { zipcode, number } = address;

    if (!zipcode || !number) {
      throw TypeError('Both zipcode and number are required');
    }

    return axios.get(
      `/kandidaat/zipcode_check.json?zipcode=${zipcode}&number=${number}`
    );
  }

  static addGoogleTagManagementData(signupEventLabel) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ...GTM_CONFIGS,
      eventLabel: signupEventLabel
    });
  }

  static captureError({
    extra,
    type = 'error',
    title = err => err.message
  } = {}) {
    return error => {
      const isNotNetworkError = !!error.response;
      const errorData = getAxiosErrorData(error);
      if (isNotNetworkError) {
        Sentry.withScope(scope => {
          each({ ...extra, ...errorData }, (data, key) => {
            scope.setExtra(key, data);
          });
          Sentry.captureMessage(title(errorData), type);
        });
      }
      return Promise.reject(error);
    };
  }
}
